import { memo, useTransition } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { useCurrentTabLink } from '@/hooks/use-current-tablink'

import { HasRole } from '@/shared/auth/components/has-role'
import { ROLES } from '@/shared/auth/constants'

import { TabLinkItem, TabsLink } from '@/components/common/tab-link'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'

type Tabs =
	| 'alerts'
	| 'triggered-alerts'
	| 'event-definitions'
	| 'automation-settings'

const TabsMenu = () => {
	const navigate = useNavigate()
	const currentTab = useCurrentTabLink()

	const [_isPending, startTransition] = useTransition()

	const handleTabChange = (tab: Tabs) => {
		// Navigate with startTransition
		startTransition(() => {
			navigate({
				to: `/graylog/${tab}`,
			})
		})
	}

	return (
		<>
			<TabsLink className="hidden h-10 sm:inline-flex">
				<TabLinkItem to="/graylog/alerts">Alerts</TabLinkItem>
				<TabLinkItem to="/graylog/triggered-alerts">
					Triggered Alerts
				</TabLinkItem>
				<TabLinkItem to="/graylog/event-definitions">
					Event Definitions
				</TabLinkItem>
				<HasRole role={ROLES.Analyst}>
					<TabLinkItem to="/graylog/automation-settings">
						Automation Settings
					</TabLinkItem>
				</HasRole>
			</TabsLink>

			{/* Mobile */}
			<div className="flex sm:hidden">
				{/*  We pass 'alerts' as default value to keep the Select controlled */}
				<Select
					value={currentTab || 'alerts'}
					onValueChange={(value) => handleTabChange(value as Tabs)}
				>
					<SelectTrigger>
						<SelectValue data-placeholder="Select Tab" />
					</SelectTrigger>

					<SelectContent>
						<SelectItem value="alerts">Alerts</SelectItem>
						<SelectItem value="triggered-alerts">Triggered Alerts</SelectItem>
						<SelectItem value="event-definitions">Event Definitions</SelectItem>
						<HasRole role={ROLES.Analyst}>
							<SelectItem value="automation-settings">
								Automation Settings
							</SelectItem>
						</HasRole>
					</SelectContent>
				</Select>
			</div>
		</>
	)
}

export const GraylogTabs = memo(TabsMenu)
